import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Button, Input, Modal, Form, Select, 
  Card, Row, Col, Space, Tag, Statistic,
  Typography, Avatar, Tooltip, Layout, theme,
  Radio, Alert
} from 'antd';
import { 
  FaPhoneAlt, FaUser, FaMapMarkerAlt, FaNotesMedical, 
  FaCalendarAlt, FaPlus, FaSearch, FaQuestionCircle, 
  FaIdCard, FaExclamationTriangle
} from 'react-icons/fa';
import { 
  SearchOutlined, PhoneOutlined, UserOutlined, MedicineBoxOutlined,
  EnvironmentOutlined, CalendarOutlined, WarningOutlined
} from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Highlighter from 'react-highlight-words';
import api from '../api/Api';

const { Title, Text } = Typography;
const { Option } = Select;
const { useToken } = theme;
const { Content } = Layout;

// ─── Normaliza o tipo para sempre comparar em minúsculo ───────────────────────
// Backend pode retornar "Engano", "ENGANO", "engano" — normalizamos tudo
const normalizarTipo = (tipo) => (tipo || '').toLowerCase();

const Chamada = () => {
  const { token } = useToken();
  
  const [chamadas,           setChamadas]           = useState([]);
  const [patologias,         setPatologias]         = useState([]);
  const [questionarios,      setQuestionarios]      = useState([]);
  const [provincias,         setProvincias]         = useState([]);
  const [isAddModalVisible,  setIsAddModalVisible]  = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form]                                      = Form.useForm();
  const [chamadaToDelete,    setChamadaToDelete]    = useState(null);
  const [respostaQuestionario, setRespostaQuestionario] = useState('');
  const [searchText,         setSearchText]         = useState('');
  const [searchedColumn,     setSearchedColumn]     = useState('');
  const [searchInput,        setSearchInput]        = useState('');
  const [loading,            setLoading]            = useState(false);
  const [loadingSubmit,      setLoadingSubmit]      = useState(false);

  // ─── FIX 2: tipoChamada controlado APENAS pelo state, sem initialValue no Form ──
  const [tipoChamada, setTipoChamada] = useState('IPU');

  const searchRef = useRef(null);

  useEffect(() => {
    document.title = "TECH NEXT CRM - CHAMADAS";
    fetchChamada();
    fetchProvincia();
    fetchPatologia();
    fetchFaq();
  }, []);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchChamada = async () => {
    try {
      setLoading(true);
      const response = await api.ListChamada2();

      if (Array.isArray(response)) {
        const dadosFormatados = response
          .sort((a, b) => b.id - a.id)
          .map(item => ({
            ...item,
            // ─── FIX 1: normaliza tipo na origem para evitar comparações case-sensitive ───
            tipo: normalizarTipo(item.tipo), // sempre 'ipu' ou 'engano'
            data: formatarData(item.created_at),
          }));

        setChamadas(dadosFormatados);
      }
    } catch (error) {
      console.error("Erro ao buscar chamadas:", error);
      sms_error("Erro ao carregar chamadas.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProvincia = async () => {
    try {
      const response = await api.ListProvincia2();
      if (Array.isArray(response)) {
        setProvincias(response.map(item => ({ id: item.id, descricao: item.descricao })));
      }
    } catch (error) {
      console.error("Erro ao buscar províncias:", error);
    }
  };

  const fetchPatologia = async () => {
    try {
      const response = await api.ListPatologia();
      if (Array.isArray(response)) {
        setPatologias(response.map(item => ({ id: item.id, descricao: item.descricao })));
      }
    } catch (error) {
      console.error("Erro ao buscar patologias:", error);
    }
  };

  const fetchFaq = async () => {
    try {
      const response = await api.ListFaq2();
      if (Array.isArray(response)) {
        setQuestionarios(response.map(item => ({
          id: item.id,
          resposta: item.resposta,
          pergunta: item.pergunta,
        })));
      }
    } catch (error) {
      console.error("Erro ao buscar FAQs:", error);
    }
  };

  // ── Toasts ─────────────────────────────────────────────────────────────────
  const sms_error   = (sms) => toast.error(sms,   { position: "top-right", autoClose: 3000 });
  const sms_success = (sms) => toast.success(sms, { position: "top-right", autoClose: 3000 });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatarData = (dataString) => {
    if (!dataString) return '—';
    const data    = new Date(dataString);
    const dia     = String(data.getDate()).padStart(2, '0');
    const mes     = String(data.getMonth() + 1).padStart(2, '0');
    const ano     = data.getFullYear();
    const horas   = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  };

  const handleQuestionarioChange = async (idQuestionario) => {
    setRespostaQuestionario('');
    form.setFieldsValue({ id_questionario: idQuestionario });
    const questionario = questionarios.find(q => q.id === idQuestionario);
    setRespostaQuestionario(questionario?.resposta || 'Sem resposta disponível.');
  };

  // ── FIX 2: ao trocar tipo, reseta o form inteiro e mantém state sincronizado ──
  const handleTipoChamadaChange = (e) => {
    const novoTipo = e.target.value;
    setTipoChamada(novoTipo);
    setRespostaQuestionario('');
    // resetFields para limpar campos do tipo anterior,
    // depois reaplica o tipo para o Radio não perder o valor
    form.resetFields();
    // pequeno timeout para o reset terminar antes de re-setar o campo
    setTimeout(() => {
      form.setFieldsValue({ tipo_chamada: novoTipo });
    }, 0);
  };

  const fecharModal = () => {
    form.resetFields();
    setRespostaQuestionario('');
    setTipoChamada('IPU');
    setIsAddModalVisible(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleAdd = async (values) => {
    try {
      setLoadingSubmit(true);

      const user = JSON.parse(localStorage.getItem('user') || '{}');

      let payload;

      if (tipoChamada === 'IPU') {
        payload = {
          tipo:         'IPU',
          id_user:      user?.id,
          sintomas:     values.sintomas     || "",
          nome:         values.nome,
          bi:           values.bi           || "",
          telefone:     values.telefone,
          id_patologia: values.id_patologia,
          municipio:    values.id_municipio,
          id_provincia: values.id_provincia,
          id_faq:       values.id_questionario,
        };
      } else {
        payload = {
          tipo:    'Engano',
          id_user: user?.id,
          telefone: values.telefone_engano,
          obs:     values.observacao || "Chamada registrada como engano",
        };
      }

      const response = await api.AddChamada2(payload);

      if (response?.success) {
        sms_success(tipoChamada === 'IPU' ? 'Chamada IPU registrada com sucesso!' : 'Chamada de engano registrada com sucesso!');
        fecharModal();
        fetchChamada();
      } else {
        sms_error("Erro ao registrar chamada.");
      }
    } catch (error) {
      console.error(error);
      sms_error("Erro ao registrar chamada.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  // ── Pesquisa por coluna ────────────────────────────────────────────────────
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchRef}
          placeholder={`Pesquisar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          prefix={<SearchOutlined />}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Pesquisar
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? token.colorPrimary : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: token.colorWarningBg, color: token.colorWarningText }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ''}
        />
      ) : (
        text
      ),
  });

  // ── Colunas ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 110,
      filters: [
        { text: 'IPU',    value: 'ipu'    },
        { text: 'Engano', value: 'engano' },
      ],
      // ─── FIX 1: comparação normalizada ────────────────────────────────────
      onFilter: (value, record) => normalizarTipo(record.tipo) === value,
      render: (tipo) => {
        const isEngano = normalizarTipo(tipo) === 'engano';
        return (
          <Tag
            color={isEngano ? 'orange' : 'blue'}
            icon={isEngano ? <WarningOutlined /> : <PhoneOutlined />}
          >
            {isEngano ? 'Engano' : 'IPU'}
          </Tag>
        );
      },
    },
    {
      title: 'Utente / Telefone',
      dataIndex: 'nome',
      key: 'nome',
      width: 190,
      ...getColumnSearchProps('nome'),
      render: (text, record) => {
        const isEngano = normalizarTipo(record.tipo) === 'engano';
        return (
          <Space>
            <Avatar
              size="small"
              style={{ backgroundColor: isEngano ? token.colorWarning : token.colorPrimary }}
            >
              {isEngano ? <WarningOutlined /> : <UserOutlined />}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500 }}>{text || <Text type="secondary">—</Text>}</div>
              {record.telefone && (
                <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                  {record.telefone}
                </div>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Observação',
      key: 'sintomas_obs',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const isEngano = normalizarTipo(record.tipo) === 'engano';
        const texto    = isEngano ? record.obs : record.sintomas;
        return texto ? (
          <Tooltip title={texto}>
            <span style={{ color: isEngano ? token.colorWarningText : token.colorText }}>
              {texto}
            </span>
          </Tooltip>
        ) : <Text type="secondary">—</Text>;
      },
    },
 
    {
      title: 'Endereço',
      key: 'localizacao',
      width: 180,
      render: (_, record) => {
        const isEngano = normalizarTipo(record.tipo) === 'engano';
        return isEngano
          ? <Tag color="default">Não informado</Tag>
          : (
            <Space direction="vertical" size={0}>
              <div>
                <EnvironmentOutlined style={{ marginRight: 4, color: token.colorSuccess }} />
                {record.municipio || '—'}
              </div>
              <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                {record.provincia || '—'}
              </div>
            </Space>
          );
      },
    },
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      width: 140,
      sorter: (a, b) => {
        const toDate = (str) => str
          ? new Date(str.split(' ')[0].split('/').reverse().join('-'))
          : new Date(0);
        return toDate(a.data) - toDate(b.data);
      },
      ...getColumnSearchProps('data'),
      render: (text, record) => {
        const isEngano = normalizarTipo(record.tipo) === 'engano';
        return (
          <Tag icon={<CalendarOutlined />} color={isEngano ? 'default' : 'blue'}>
            {text}
          </Tag>
        );
      },
    },
  ];

  // ── Contadores com normalização ────────────────────────────────────────────
  const totalIPU    = chamadas.filter(c => normalizarTipo(c.tipo) === 'ipu').length;
  const totalEngano = chamadas.filter(c => normalizarTipo(c.tipo) === 'engano').length;
  const totalUlt24h = chamadas.filter(c => {
    if (!c.created_at) return false;
    return (Date.now() - new Date(c.created_at).getTime()) <= 24 * 60 * 60 * 1000;
  }).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Content style={{ padding: '24px' }}>
        <ToastContainer />

        <Card
          title={
            <Space>
              <FaPhoneAlt style={{ color: token.colorPrimary, fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Gestão de Chamadas</Title>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<FaPlus />}
              onClick={() => setIsAddModalVisible(true)}
            >
              Nova Chamada
            </Button>
          }
          style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
          {/* Estatísticas */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Total de Chamadas"
                  value={chamadas.length}
                  prefix={<FaPhoneAlt />}
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Chamadas IPU"
                  value={totalIPU}
                  prefix={<FaNotesMedical />}
                  valueStyle={{ color: token.colorSuccess }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Chamadas Engano"
                  value={totalEngano}
                  prefix={<FaExclamationTriangle />}
                  valueStyle={{ color: token.colorWarning }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <Statistic
                  title="Últimas 24h"
                  value={totalUlt24h}
                  prefix={<FaCalendarAlt />}
                  valueStyle={{ color: token.colorError }}
                />
              </Card>
            </Col>
          </Row>

          {/* Pesquisa global */}
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Pesquisar em todas as colunas..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ maxWidth: 400 }}
              allowClear
            />
          </div>

          <Table
            columns={columns}
            dataSource={chamadas.filter(chamada =>
              !searchInput ||
              Object.values(chamada).some(value =>
                value?.toString().toLowerCase().includes(searchInput.toLowerCase())
              )
            )}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} chamadas`,
              pageSizeOptions: ['10', '20', '50', '100'],
              defaultPageSize: 8,
            }}
          />
        </Card>

        {/* ── Modal Nova Chamada ───────────────────────────────────────────── */}
        <Modal
          title={
            <Space>
              <FaPlus style={{ color: token.colorPrimary }} />
              <span>Nova Chamada</span>
            </Space>
          }
          open={isAddModalVisible}
          onCancel={fecharModal}
          footer={null}
          width={tipoChamada === 'IPU' ? 800 : 500}
          centered
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleAdd}>

            {/* ─── FIX 2: Radio controlado pelo state, sem initialValue conflituoso ── */}
            <Form.Item
              label="Tipo de Chamada"
              name="tipo_chamada"
            >
              <Radio.Group
                value={tipoChamada}          // controlado pelo state
                onChange={handleTipoChamadaChange}
                style={{ width: '100%' }}
              >
                <Radio.Button value="IPU"    style={{ width: '50%', textAlign: 'center' }}>
                  <Space><MedicineBoxOutlined /> IPU</Space>
                </Radio.Button>
                <Radio.Button value="ENGANO" style={{ width: '50%', textAlign: 'center' }}>
                  <Space><WarningOutlined /> Engano</Space>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {/* Formulário IPU */}
            {tipoChamada === 'IPU' ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="nome"
                      label="Nome"
                      rules={[{ required: true, message: 'Nome é obrigatório' }]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Nome do utente" size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="telefone"
                      label="Telefone"
                      rules={[{ required: true, message: 'Telefone é obrigatório' }]}
                    >
                      <Input prefix={<PhoneOutlined />} placeholder="Número de telefone" size="large" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="id_provincia"
                      label="Província"
                      rules={[{ required: true, message: 'Província é obrigatória' }]}
                    >
                      <Select suffixIcon={<EnvironmentOutlined />} size="large" placeholder="Selecione a província">
                        {provincias.map(p => (
                          <Option key={p.id} value={p.id}>{p.descricao}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="id_municipio"
                      label="Município"
                      rules={[{ required: true, message: 'Município é obrigatório' }]}
                    >
                      <Input size="large" placeholder="Nome do município" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="id_questionario"
                  label="Questionário"
                  rules={[{ required: true, message: 'Selecione um questionário' }]}
                >
                  <Select
                    onChange={handleQuestionarioChange}
                    size="large"
                    placeholder="Selecione um questionário"
                    suffixIcon={<FaQuestionCircle />}
                  >
                    {questionarios.map(q => (
                      <Option key={q.id} value={q.id}>{q.pergunta}</Option>
                    ))}
                  </Select>
                </Form.Item>

                {respostaQuestionario && (
                  <Card
                    size="small"
                    title="Resposta do Questionário"
                    style={{ marginBottom: 16, borderLeft: `4px solid ${token.colorPrimary}` }}
                  >
                    <Text>{respostaQuestionario}</Text>
                  </Card>
                )}

                <Form.Item name="bi" label="BI (Opcional)">
                  <Input prefix={<FaIdCard />} placeholder="Número do bilhete de identidade" size="large" />
                </Form.Item>
              </>
            ) : (
              /* Formulário ENGANO */
              <>
                <Alert
                  message="Registro de Chamada Enganosa"
                  description="Esta chamada será registrada apenas com número de telefone e observação."
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Form.Item
                  name="telefone_engano"
                  label="Número de Telefone"
                  rules={[{ required: true, message: 'Número de telefone é obrigatório' }]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="Número que fez a chamada enganosa"
                    size="large"
                  />
                </Form.Item>

                <Form.Item name="observacao" label="Observação">
                  <Input.TextArea
                    rows={4}
                    placeholder="Observações sobre a chamada enganosa"
                    showCount
                    maxLength={500}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={fecharModal} size="large">Cancelar</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  icon={<FaPlus />}
                  loading={loadingSubmit}
                >
                  {tipoChamada === 'IPU' ? 'Criar Chamada' : 'Registrar Engano'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* ── Modal Confirmação Exclusão ───────────────────────────────────── */}
        <Modal
          title="Confirmar Exclusão"
          open={isDeleteModalVisible}
          onOk={async () => {
            try {
              sms_success('Chamada excluída com sucesso!');
              fetchChamada();
            } catch (error) {
              sms_error('Erro ao excluir chamada: ' + error.message);
            }
            setIsDeleteModalVisible(false);
            setChamadaToDelete(null);
          }}
          onCancel={() => {
            setIsDeleteModalVisible(false);
            setChamadaToDelete(null);
          }}
          okText="Excluir"
          cancelText="Cancelar"
          okType="danger"
        >
          <p>Tem certeza que deseja excluir esta chamada?</p>
          <p>Esta ação não pode ser desfeita.</p>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Chamada;